import { FALLBACK_LANGUAGE, isLanguage } from './languages';
import type { Language } from './language';

/** Where a returning visitor's choice is kept, under the same key the live static site uses. */
export const LANGUAGE_STORAGE_KEY = 'lang';

/**
 * Which language a visitor with no prefix in the URL should land on.
 *
 * **This is a parity fix, not a new feature.** The live site reads `navigator.language` — a primary
 * language starting with `ru` gets Russian, everything else Kazakh — and remembers the choice in
 * `localStorage`. The React app redirected everyone to `/kk` unconditionally, so on cutover day
 * roughly half the visitors would have landed in the wrong language, which is invisible in testing
 * and obvious to them.
 *
 * **It deliberately does not consult `PublicSite.DefaultLanguage`.** The redirect happens before
 * anything is rendered and must not wait on a network call; a setting that decides this would have
 * to be prerendered into the bundle, which is the open rendering decision rather than this one.
 *
 * A remembered choice wins over the browser's, because it is the more recent statement of the same
 * preference — and because that is what the live site does.
 *
 * **Pure, and it has to stay that way.** Everything under `core/i18n` is imported by the build's
 * prerenderer as well as by the application, and the Node project it compiles under has no DOM —
 * so reading `localStorage` here would be a type error rather than a runtime surprise. The reading
 * and writing live in `app/language-memory.ts`.
 */
export function preferredLanguage(
    navigatorLanguages: readonly string[] | undefined,
    remembered?: string | null
): Language {
    if (isLanguage(remembered)) {
        return remembered;
    }

    const primary = navigatorLanguages?.[0];

    // `ru`, `ru-RU`, `ru-KZ` — the tag's language subtag, matched case-insensitively because the
    // header is not required to be lower case.
    if (primary !== undefined && primary.toLowerCase().startsWith('ru')) {
        return 'ru';
    }

    return FALLBACK_LANGUAGE;
}
